import type { CSSProperties, ReactNode } from "react";
import { useEffect, useMemo, useState } from "react";
import { Navigate, NavLink, Route, Routes } from "react-router-dom";
import {
  useDomainRouterMutation,
  useDomainRouterQuery,
} from "../../../state/domainRouterHooks";
import {
  ModuleSectionNav,
  type ModuleSectionNavItem,
} from "../../../components/navigation/ModuleSectionNav";
import { YstRouteTransition } from "../../../components/motion/YstRouteTransition";
import { SupportErrorMessage } from "../../../components/overlays/EngineeringSupportBanner";

type TipOverview = {
  settings: {
    enabled: boolean;
    allowAnonymous: boolean;
    allowMessages: boolean;
    defaultTipSuggestionsCents: number[];
    notificationEmail?: string | null;
    updatedAt: number;
  };
  totals: {
    tipCount: number;
    totalCents: number;
  };
  teacherTotals: Array<{
    teacherId: string;
    teacherName: string;
    division: string;
    totalCents: number;
    tipCount: number;
  }>;
};

type TipSettings = {
  enabled: boolean;
  allowAnonymous: boolean;
  allowMessages: boolean;
  defaultTipSuggestionsCents: number[];
  notificationEmail?: string | null;
  updatedAt: number;
};

type TipNotification = {
  notificationId: string;
  title: string;
  body: string;
  channel: "email" | "in_app";
  audience: "staff" | "teacher";
  createdAt: number;
};

const TIP_SECTION_ITEMS: ModuleSectionNavItem[] = [
  { id: "overview", label: "Overview", to: "/school/tip-a-teacher", end: true },
  { id: "settings", label: "Settings", to: "/school/tip-a-teacher/settings" },
  {
    id: "notifications",
    label: "Notifications",
    to: "/school/tip-a-teacher/notifications",
  },
];

const formatCurrency = (cents: number) =>
  `$${(cents / 100).toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })}`;

const formatTimestamp = (value: number) =>
  new Date(value * 1000).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

const Shell = ({
  title,
  subtitle,
  testId,
  children,
}: {
  title?: string;
  subtitle: string;
  testId: string;
  children: ReactNode;
}) => (
  <main
    data-testid={testId}
    data-route-family="source-faithful"
    data-route-ready="true"
    className="source-faithful-shell"
    style={{ "--ystnj-source-faithful-max-width": "1024px" } as CSSProperties}
  >
    <YstRouteTransition className="source-faithful-container">
      <div className="mb-4">
        {title ? <h2 className="yst-surface-title">{title}</h2> : null}
        <p
          className={
            title
              ? "yst-surface-eyebrow !text-text-secondary mt-1"
              : "yst-surface-eyebrow !text-text-secondary"
          }
        >
          {subtitle}
        </p>
      </div>
      <ModuleSectionNav
        items={TIP_SECTION_ITEMS}
        ariaLabel="Tip a Teacher sections"
        desktopClassName="source-faithful-tabs mb-4"
        testIds={{
          root: "school-tip-section-nav",
          itemPrefix: "school-tip-section-nav-item",
          mobileTrigger: "school-tip-section-nav-mobile-trigger",
          mobileDialog: "school-tip-section-nav-mobile-dialog",
          mobileItemPrefix: "school-tip-section-nav-mobile-item",
        }}
      />
      {children}
    </YstRouteTransition>
  </main>
);

const EmptyState = ({ message }: { message: string }) => (
  <div className="rounded-[12px] border border-dashed border-[var(--border)] bg-[var(--surface-light)] px-6 py-10 text-center text-sm text-[var(--text-secondary)]">
    {message}
  </div>
);

const OverviewPage = () => {
  const overviewQuery = useDomainRouterQuery("getTipSchoolOverview", {});
  const overview = (overviewQuery.data as TipOverview | undefined) ?? {
    settings: {
      enabled: true,
      allowAnonymous: true,
      allowMessages: true,
      defaultTipSuggestionsCents: [],
      notificationEmail: null,
      updatedAt: 0,
    },
    totals: { tipCount: 0, totalCents: 0 },
    teacherTotals: [],
  };

  return (
    <Shell
      subtitle="Persisted school program overview"
      testId="school-tip-a-teacher-overview"
    >
      <div className="source-faithful-kpiGrid">
        {[
          ["Program status", overview.settings.enabled ? "Live" : "Paused"],
          ["Tips recorded", String(overview.totals.tipCount)],
          ["Total raised", formatCurrency(overview.totals.totalCents)],
          [
            "Last updated",
            overview.settings.updatedAt
              ? formatTimestamp(overview.settings.updatedAt)
              : "Not yet updated",
          ],
        ].map(([label, value]) => (
          <article
            key={label}
            className="rounded-[12px] border border-[var(--border)] bg-[var(--surface-light)] p-5 shadow-sm"
          >
            <p className="text-xs font-semibold uppercase tracking-[0.05em] text-[var(--text-secondary)]">
              {label}
            </p>
            <p className="mt-3 text-2xl font-semibold text-[var(--iron-grey)]">
              {value}
            </p>
          </article>
        ))}
      </div>
      <div className="mt-6 grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
        <div className="overflow-hidden rounded-[12px] border border-[var(--border)] bg-[var(--surface-light)] shadow-sm">
          <div className="border-b border-[var(--border)] px-4 py-3">
            <h2 className="text-[16px] font-semibold text-[var(--iron-grey)]">
              Teacher totals
            </h2>
          </div>
          {!overview.teacherTotals.length ? (
            <EmptyState message="No persisted tip history has been recorded yet." />
          ) : (
            overview.teacherTotals.map((item, index) => (
              <div
                key={item.teacherId}
                className={`flex items-center justify-between gap-4 px-4 py-4 ${index < overview.teacherTotals.length - 1 ? "border-b border-[var(--border)]" : ""}`}
              >
                <div>
                  <p className="font-semibold text-[var(--iron-grey)]">
                    {item.teacherName}
                  </p>
                  <p className="text-sm text-[var(--text-secondary)]">
                    {item.division} · {item.tipCount} tips
                  </p>
                </div>
                <span className="font-semibold text-[var(--iron-grey)]">
                  {formatCurrency(item.totalCents)}
                </span>
              </div>
            ))
          )}
        </div>
        <div className="rounded-[12px] border border-[var(--border)] bg-[var(--surface-light)] p-5 shadow-sm">
          <h2 className="text-[16px] font-semibold text-[var(--iron-grey)]">
            Program settings snapshot
          </h2>
          <div className="mt-4 space-y-3 text-sm text-[var(--iron-grey)]">
            <div className="flex items-center justify-between gap-4">
              <span>Anonymous tips</span>
              <span>
                {overview.settings.allowAnonymous ? "Allowed" : "Blocked"}
              </span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span>Parent messages</span>
              <span>
                {overview.settings.allowMessages ? "Allowed" : "Blocked"}
              </span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span>Notification email</span>
              <span>
                {overview.settings.notificationEmail || "Not configured"}
              </span>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.05em] text-[var(--text-secondary)]">
                Suggested tip amounts
              </p>
              <p className="mt-2">
                {overview.settings.defaultTipSuggestionsCents
                  .map(formatCurrency)
                  .join(" · ") || "No suggestions configured"}
              </p>
            </div>
          </div>
        </div>
      </div>
    </Shell>
  );
};

const SettingsPage = () => {
  const settingsQuery = useDomainRouterQuery("getTipProgramSettings", {});
  const [updateSettings, updateState] = useDomainRouterMutation(
    "updateTipProgramSettings",
  );
  const settings = (settingsQuery.data as TipSettings | undefined) ?? {
    enabled: true,
    allowAnonymous: true,
    allowMessages: true,
    defaultTipSuggestionsCents: [1800, 3600, 5400, 7200],
    notificationEmail: "",
    updatedAt: 0,
  };
  const [form, setForm] = useState({
    enabled: true,
    allowAnonymous: true,
    allowMessages: true,
    defaultTipSuggestions: "18, 36, 54, 72",
    notificationEmail: "",
  });
  const [notice, setNotice] = useState("");

  useEffect(() => {
    setForm({
      enabled: settings.enabled,
      allowAnonymous: settings.allowAnonymous,
      allowMessages: settings.allowMessages,
      defaultTipSuggestions: settings.defaultTipSuggestionsCents
        .map((value) => String(value / 100))
        .join(", "),
      notificationEmail: settings.notificationEmail ?? "",
    });
  }, [
    settings.allowAnonymous,
    settings.allowMessages,
    settings.defaultTipSuggestionsCents,
    settings.enabled,
    settings.notificationEmail,
  ]);

  const parsedSuggestions = useMemo(
    () =>
      form.defaultTipSuggestions
        .split(",")
        .map((value) => Number(value.trim()))
        .filter((value) => Number.isFinite(value) && value > 0)
        .map((value) => Math.round(value * 100)),
    [form.defaultTipSuggestions],
  );

  const submit = async () => {
    await updateSettings({
      input: {
        enabled: form.enabled,
        allowAnonymous: form.allowAnonymous,
        allowMessages: form.allowMessages,
        defaultTipSuggestionsCents: parsedSuggestions,
        notificationEmail: form.notificationEmail.trim() || null,
      },
    });
    setNotice("Program settings saved.");
  };

  return (
    <Shell
      title="Tip program settings"
      subtitle="Persisted school-side configuration"
      testId="school-tip-a-teacher-settings"
    >
      <div className="rounded-[12px] border border-[var(--border)] bg-[var(--surface-light)] p-6 shadow-sm">
        <div className="grid gap-4 md:grid-cols-2">
          {(
            [
              ["Program enabled", "enabled"],
              ["Allow anonymous tips", "allowAnonymous"],
              ["Allow parent messages", "allowMessages"],
            ] as const
          ).map(([label, key]) => (
            <label
              key={key}
              className="flex items-center justify-between rounded-[10px] border border-[var(--border)] bg-white px-4 py-3 text-sm font-medium text-[var(--iron-grey)]"
            >
              {label}
              <input
                type="checkbox"
                checked={form[key]}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    [key]: event.target.checked,
                  }))
                }
                className="h-4 w-4 rounded border-[var(--border)] text-[var(--primary)]"
              />
            </label>
          ))}
        </div>
        <label className="mt-4 block text-sm font-medium text-[var(--iron-grey)]">
          Suggested tip amounts (dollars, comma-separated)
          <input
            value={form.defaultTipSuggestions}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                defaultTipSuggestions: event.target.value,
              }))
            }
            className="mt-2 h-11 w-full rounded-[8px] border border-[var(--border)] bg-white px-3 text-[14px] text-[var(--iron-grey)]"
          />
        </label>
        <label className="mt-4 block text-sm font-medium text-[var(--iron-grey)]">
          Notification email
          <input
            type="email"
            value={form.notificationEmail}
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                notificationEmail: event.target.value,
              }))
            }
            className="mt-2 h-11 w-full rounded-[8px] border border-[var(--border)] bg-white px-3 text-[14px] text-[var(--iron-grey)]"
          />
        </label>
        {notice ? (
          <p className="mt-4 text-sm font-medium text-[var(--primary)]">
            {notice}
          </p>
        ) : null}
        {updateState.error ? (
          <SupportErrorMessage className="mt-4 text-sm">
            The settings update failed.
          </SupportErrorMessage>
        ) : null}
        <button
          type="button"
          onClick={() => void submit()}
          disabled={updateState.isLoading}
          className="mt-4 inline-flex h-11 items-center justify-center rounded-[8px] bg-[var(--primary)] px-5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-70"
        >
          {updateState.isLoading ? "Saving…" : "Save settings"}
        </button>
      </div>
    </Shell>
  );
};

const NotificationsPage = () => {
  const notificationsQuery = useDomainRouterQuery("listTipNotifications", {});
  const [sendTest, sendState] = useDomainRouterMutation(
    "sendTipNotificationTest",
  );
  const [title, setTitle] = useState("Tip a Teacher reminder");
  const [body, setBody] = useState(
    "Please review the current tip totals and staff notification routing.",
  );
  const [channel, setChannel] = useState<"email" | "in_app">("email");
  const [audience, setAudience] = useState<"staff" | "teacher">("staff");
  const [notice, setNotice] = useState("");

  const notifications =
    (notificationsQuery.data as { items?: TipNotification[] } | undefined)
      ?.items ?? [];

  const submit = async () => {
    await sendTest({
      input: {
        title,
        body,
        channel,
        audience,
      },
    });
    setNotice("Notification test saved.");
  };

  return (
    <Shell
      title="Tip notifications"
      subtitle="Persisted notification activity"
      testId="school-tip-a-teacher-notifications"
    >
      <div className="grid gap-6 lg:grid-cols-[0.9fr_1.1fr]">
        <div className="rounded-[12px] border border-[var(--border)] bg-[var(--surface-light)] p-6 shadow-sm">
          <h2 className="text-[16px] font-semibold text-[var(--iron-grey)]">
            Send test notification
          </h2>
          <label className="mt-4 block text-sm font-medium text-[var(--iron-grey)]">
            Title
            <input
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              className="mt-2 h-11 w-full rounded-[8px] border border-[var(--border)] bg-white px-3 text-[14px] text-[var(--iron-grey)]"
            />
          </label>
          <label className="mt-4 block text-sm font-medium text-[var(--iron-grey)]">
            Body
            <textarea
              value={body}
              onChange={(event) => setBody(event.target.value)}
              className="mt-2 min-h-28 w-full rounded-[8px] border border-[var(--border)] bg-white px-3 py-3 text-[14px] text-[var(--iron-grey)]"
            />
          </label>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <label className="text-sm font-medium text-[var(--iron-grey)]">
              Channel
              <select
                value={channel}
                onChange={(event) =>
                  setChannel(event.target.value as "email" | "in_app")
                }
                className="mt-2 h-11 w-full rounded-[8px] border border-[var(--border)] bg-white px-3 text-[14px] text-[var(--iron-grey)]"
              >
                <option value="email">Email</option>
                <option value="in_app">In-app</option>
              </select>
            </label>
            <label className="text-sm font-medium text-[var(--iron-grey)]">
              Audience
              <select
                value={audience}
                onChange={(event) =>
                  setAudience(event.target.value as "staff" | "teacher")
                }
                className="mt-2 h-11 w-full rounded-[8px] border border-[var(--border)] bg-white px-3 text-[14px] text-[var(--iron-grey)]"
              >
                <option value="staff">Staff</option>
                <option value="teacher">Teacher</option>
              </select>
            </label>
          </div>
          {notice ? (
            <p className="mt-4 text-sm font-medium text-[var(--primary)]">
              {notice}
            </p>
          ) : null}
          {sendState.error ? (
            <SupportErrorMessage className="mt-4 text-sm">
              The notification test could not be saved.
            </SupportErrorMessage>
          ) : null}
          <button
            type="button"
            onClick={() => void submit()}
            disabled={sendState.isLoading}
            className="mt-4 inline-flex h-11 items-center justify-center rounded-[8px] bg-[var(--primary)] px-5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-70"
          >
            {sendState.isLoading ? "Saving…" : "Save notification test"}
          </button>
        </div>
        <div className="overflow-hidden rounded-[12px] border border-[var(--border)] bg-[var(--surface-light)] shadow-sm">
          <div className="border-b border-[var(--border)] px-4 py-3">
            <h2 className="text-[16px] font-semibold text-[var(--iron-grey)]">
              Notification log
            </h2>
          </div>
          {!notifications.length ? (
            <EmptyState message="No persisted notification events exist yet." />
          ) : (
            notifications.map((notification, index) => (
              <div
                key={notification.notificationId}
                className={`px-4 py-4 ${index < notifications.length - 1 ? "border-b border-[var(--border)]" : ""}`}
              >
                <div className="flex items-center justify-between gap-4">
                  <p className="font-semibold text-[var(--iron-grey)]">
                    {notification.title}
                  </p>
                  <span className="text-xs font-semibold uppercase tracking-[0.05em] text-[var(--text-secondary)]">
                    {notification.channel} · {notification.audience}
                  </span>
                </div>
                <p className="mt-2 text-sm text-[var(--text-secondary)]">
                  {notification.body}
                </p>
                <p className="mt-3 text-xs text-[var(--text-secondary)]">
                  {formatTimestamp(notification.createdAt)}
                </p>
              </div>
            ))
          )}
        </div>
      </div>
    </Shell>
  );
};

export function SchoolTipATeacherSourceFaithfulRoutes() {
  return (
    <Routes>
      <Route index element={<OverviewPage />} />
      <Route path="settings" element={<SettingsPage />} />
      <Route path="notifications" element={<NotificationsPage />} />
      <Route path="*" element={<Navigate to="." replace />} />
    </Routes>
  );
}
